'use client';

import React, { useState, useEffect } from 'react';
import { PlayCircle, StopCircle, Coffee } from 'lucide-react';
import { toast } from 'sonner';

const activeShifts = [
  { id: 'shift-active-001', name: 'Daniela Reyes', role: 'RN', facility: 'Riverside Medical', clockIn: '07:02 AM', elapsed: '6h 58m' },
  { id: 'shift-active-002', name: 'Tomás Herrera', role: 'RN', facility: "St. Luke\'s North", clockIn: '07:15 AM', elapsed: '6h 45m' },
  { id: 'shift-active-003', name: 'Yuki Tanaka', role: 'RN', facility: 'Memorial East', clockIn: '11:00 PM', elapsed: '3h 0m' },
];

export default function ClockWidget() {
  const [clockedIn, setClockedIn] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState<string>('');

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (clockedIn) {
      interval = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [clockedIn]);

  const formatElapsed = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleClockIn = () => {
    setClockedIn(true);
    setElapsedSeconds(0);
    setStartTime('Now');
    toast.success('Clocked in successfully', { description: 'Your shift timer has started.' });
  };

  const handleClockOut = () => {
    setClockedIn(false);
    toast.success('Clocked out', { description: `Shift duration: ${formatElapsed(elapsedSeconds)}` });
    setElapsedSeconds(0);
  };

  return (
    <div className="card-elevated p-5 h-full flex flex-col">
      <h3 className="text-[15px] font-600 text-foreground mb-4">Clock In / Out</h3>

      {/* Timer display */}
      <div className={`rounded-xl p-5 text-center mb-4 ${clockedIn ? 'bg-accent/10 border border-accent/20' : 'bg-muted'}`}>
        <p className="text-[11px] font-500 text-muted-foreground uppercase tracking-widest mb-2">
          {clockedIn ? 'Shift In Progress' : 'Not Clocked In'}
        </p>
        <p className={`text-3xl font-700 font-mono tabular-nums ${clockedIn ? 'text-accent' : 'text-muted-foreground'}`}>
          {formatElapsed(elapsedSeconds)}
        </p>
        {clockedIn && startTime && (
          <p className="text-[11px] text-muted-foreground mt-1">Started {startTime}</p>
        )}
      </div>

      {/* Clock in/out buttons */}
      {!clockedIn ? (
        <button
          onClick={handleClockIn}
          className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-600 mb-3"
        >
          <PlayCircle size={17} />
          Clock In
        </button>
      ) : (
        <div className="flex gap-2 mb-3">
          <button
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-lg text-sm font-600 bg-muted text-muted-foreground hover:bg-border transition-colors"
          >
            <Coffee size={15} />
            Break
          </button>
          <button
            onClick={handleClockOut}
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-lg text-sm font-600 bg-danger/10 text-danger hover:bg-danger/20 transition-colors border border-danger/20"
          >
            <StopCircle size={15} />
            Clock Out
          </button>
        </div>
      )}

      {/* Currently active */}
      <div className="mt-auto">
        <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wide mb-2">Currently Active</p>
        <ul className="space-y-2">
          {activeShifts.map((s) => (
            <li key={s.id} className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                <span className="text-[12px] text-foreground font-500 truncate">{s.name}</span>
                <span className="badge-rn text-[10px] px-1.5 py-0.5 rounded-full font-600">{s.role}</span>
              </div>
              <span className="text-[11px] font-mono-data text-muted-foreground flex-shrink-0 ml-2">{s.elapsed}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}